import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import {
  AttachmentsTicket,
  ServiceNowTicket,
} from 'src/app/models/servicenowticket.model';
import { ImageService } from 'src/app/services/profile-picture/images.service';
import { lastValueFrom } from 'rxjs';
import { ServiceNowService } from 'src/app/services/servicenow/servicenow.service';
import { CustomValidators } from 'src/app/utils/validators';
import { FileDropEntry } from '../../shared/file-drop/file-drop.entry';
import { FileSystemFileEntry } from '../../shared/file-drop/dom.types';

@Component({
  selector: 'app-servicenow-form',
  templateUrl: './servicenow-form.component.html',
  styleUrl: './servicenow-form.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ServicenowFormComponent {
  files: FileDropEntry[] = [];
  attachmentsTicket: AttachmentsTicket[] = [];
  base64String: string;
  filename: string;
  isBusy = false;
  defaultDial = '+41';
  ticketForm: FormGroup;
  error: Error = { error: { message: '', code: '' } };
  phoneValidator = '^[0-9]*$';
  NameNotNumberValidation = '^([^0-9]*)$';
  isErrorCreate = false;
  isTicketCreated = false;
  isFileUploaded = false;
  isLogin = false;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  @Output() hasCloseForm = new EventEmitter<boolean>();

  constructor(
    private _formBuilder: FormBuilder,
    private _imageService: ImageService,
    private _serviceNowService: ServiceNowService,
    private ref: ChangeDetectorRef
  ) {
    this.ticketForm = this._formBuilder.group({
      name: ['', Validators.pattern(this.NameNotNumberValidation)],
      defaultDial: [''],
      phone: [
        '',
        [Validators.pattern(this.phoneValidator), Validators.minLength(9)],
      ],
      email: [
        '',
        [
          Validators.required,
          CustomValidators.validEmail(),
          CustomValidators.EmailInterdiction(),
        ],
      ],
      eduidemail: [
        '',
        [CustomValidators.validEmail(), CustomValidators.eduidEmail()],
      ],
      title: ['', Validators.required],
      description: ['', Validators.required],
      file: [null],
    });

    this.ticketForm.get('defaultDial').setValue('+41');
  }

  dropped(files: FileDropEntry[]) {
    this.files = files;
    for (const droppedFile of files) {
      // Check if file
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        fileEntry.file(async (file: File) => {
          try {
            // Convert to base64 to store and display in cropper
            const imageData =
              await this._imageService.convertFileToBase64(file);
            const contentType = this.getImageContentType(imageData);
            const base64String = this.getBase64String(imageData);

            this.attachmentsTicket.push({
              base64Data: base64String,
              filename: droppedFile.fileEntry.name,
              contentType: contentType,
            });

            this.isFileUploaded = true;
          } catch (error) {
            console.error('Error processing file:', error);
          } finally {
            this.ref.detectChanges();
          }
        });
      }
    }
  }

  getImageContentType(imageData: string): string {
    return imageData.substring(
      imageData.indexOf(':') + 1,
      imageData.indexOf(';')
    );
  }

  getBase64String(imageData: string): string {
    return imageData.substring(imageData.indexOf(',') + 1);
  }

  deleteFile(fileName: string) {
    const index = this.attachmentsTicket.findIndex(
      e => e.filename === fileName
    );
    if (index !== -1) {
      this.attachmentsTicket.splice(index, 1);
    }
  }

  buildTicket(): ServiceNowTicket {
    const ticket: ServiceNowTicket = new ServiceNowTicket();

    ticket.shortDescription = this.title;
    let description = 'Nom et prénom : ' + this.name + '\n';
    description += 'Email de contact : ' + this.email + '\n';
    description += 'Email EDUID : ' + this.eduidEmail + '\n';
    description += 'N° Tel : ' + this.phoneNumber + '\n';
    description += 'Message : ' + this.description;

    ticket.description = description;
    ticket.sender = this.email;
    ticket.attachments = this.attachmentsTicket;

    return ticket;
  }

  async createServiceNowTicket() {
    try {
      this.isBusy = true;
      await lastValueFrom(
        this._serviceNowService.createTicket(this.buildTicket())
      ).then(() => {
        this.isTicketCreated = true;
        this.ticketForm.reset({
          name: '',
          email: '',
          eduidemail: '',
          defaultDial: '',
          phone: '',
          title: '',
          description: '',
        });
        this.attachmentsTicket = [];
        this.isFileUploaded = false;
      });
    } catch (err) {
      this.error = err;
      console.log(err);
      this.isErrorCreate = true;
    } finally {
      this.isBusy = false;
      this.ref.detectChanges();
    }
  }

  closeForms() {
    this.hasCloseForm.emit(false);
  }

  get name(): string {
    return this.ticketForm.get('name').value;
  }

  get eduidEmail(): string {
    return this.ticketForm.get('eduidemail').value;
  }

  get email(): string {
    return this.ticketForm.get('email').value;
  }

  get phoneNumber(): string {
    if (this.ticketForm.get('phone').value === '') {
      return '';
    } else {
      return (
        this.ticketForm.get('defaultDial').value +
        ' ' +
        this.ticketForm.get('phone').value
      );
    }
  }

  get title(): string {
    return this.ticketForm.get('title').value;
  }

  get description(): string {
    return this.ticketForm.get('description').value;
  }
}
